'use client'

import { Info } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import {
    useGetPlatformStatsQuery,
    type PlatformStatsData,
} from '@/api/public/platform-stats/get-platform-stats-query'
import { VisionAnimatedNumber } from '@/features/landing-page/v3/sections/vision-animated-number'
import { VISION_STATS, VISION_STATS_COLLAPSED, type VisionStat } from '@/features/landing-page/v3/sections/vision-stats-data'

/**
 * lp-v3 vision-section stat stack — acid-yellow notification cards backed by LIVE platform stats
 * (public `/platform-stats`, polled). Values roll on a wheel (VisionAnimatedNumber) whenever the poll
 * returns a new number. Collapsed (desktop) shows the first `VISION_STATS_COLLAPSED` cards in full and
 * the rest peek behind them as a faded, narrowed FAN; hovering expands the fan (pure CSS). On mobile
 * every card is shown in full.
 *
 * Client island: it polls, but the SSR-seeded `initialData` (fetched server-side in the section) means
 * the initial numbers + labels still ship in the HTML for SEO. `!` beats `.ds2`.
 */
export function VisionStats({ initialData }: { initialData?: PlatformStatsData }) {
    const { data } = useGetPlatformStatsQuery(initialData)

    // No fabricated placeholders: an absent value shows 0, never an invented figure.
    const valueOf = (stat: VisionStat) => data?.[stat.apiKey] ?? 0

    const front = VISION_STATS.slice(0, VISION_STATS_COLLAPSED)
    // Rendered top-to-bottom, farthest (most-hidden) first, so they stack behind the front cards.
    const fan = VISION_STATS.slice(VISION_STATS_COLLAPSED).reverse()

    return (
        <div className="group flex w-[300px] max-w-full flex-col">
            {fan.map((stat, index) => {
                const depth = fan.length - index // 1 = nearest the front cards, N = farthest / topmost
                // clearly faded vs the solid (opacity-1) front cards, fading further back into the deck
                const opacity = Math.max(0.1, 0.4 - (depth - 1) * 0.15)
                const scale = 1 - depth * 0.04
                const marginBottom = `-${28 + (depth - 1) * 6}px`
                return (
                    <div
                        key={stat.id}
                        style={
                            {
                                '--fan-o': String(opacity),
                                '--fan-s': String(scale),
                                '--fan-mb': marginBottom,
                                // sit BEHIND the front cards (nearest of the deck highest, still below the front z-20)
                                zIndex: 10 - depth,
                            } as React.CSSProperties
                        }
                        className={cn(
                            'relative origin-bottom transition-[opacity,scale,margin] duration-300 ease-out',
                            // mobile: full card, normal spacing (no hover to expand)
                            'mb-1.5',
                            // desktop collapsed: faded, narrowed, tucked behind the front cards
                            'md:mb-[var(--fan-mb)] md:opacity-[var(--fan-o)] md:scale-[var(--fan-s)]',
                            // desktop hover: expand the fan into full cards
                            'md:group-hover:mb-1.5 md:group-hover:scale-100 md:group-hover:opacity-100'
                        )}
                    >
                        <StatCard stat={stat} value={valueOf(stat)} />
                    </div>
                )
            })}
            {front.map((stat, index) => (
                // z-20 keeps the two front cards on top of the deck peeking behind them
                <div key={stat.id} className={cn('relative z-20', index < front.length - 1 && 'mb-1.5')}>
                    <StatCard stat={stat} value={valueOf(stat)} />
                </div>
            ))}
        </div>
    )
}

function StatCard({ stat, value }: { stat: VisionStat; value: number }) {
    return (
        <div className="flex items-center justify-between gap-4 rounded-[8px] bg-[#D6FF33] px-4 py-2.5">
            <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate text-[10px]! font-bold uppercase leading-[1.04]! tracking-normal! text-[#131210]">
                    {stat.label}
                </span>
                {stat.info && (
                    <span className="group/info relative inline-flex shrink-0">
                        <Info className="size-3 text-[#131210]/55" />
                        <span className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 w-max max-w-[200px] -translate-x-1/2 rounded-[6px] bg-[#131210] px-2.5 py-1.5 text-[11px] font-normal leading-[1.3] text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/info:opacity-100">
                            {stat.info}
                        </span>
                    </span>
                )}
            </span>
            <VisionAnimatedNumber
                value={value}
                className="shrink-0 font-[family-name:var(--font-landing-pixel)]! text-[16px]! leading-[1]! text-[#131210]"
            />
        </div>
    )
}
